import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Label,
} from '@headlessui/react';
import {
  CheckIcon,
  ChevronUpDownIcon,
  Cog6ToothIcon,
} from '@heroicons/react/20/solid';
import { useEffect, useState } from 'react';
import { Category } from '../../features/categories/types';
import { openCategoryModal } from '../../features/categories/components/CategoryFormModal';
import { fetchCategories } from '../../features/categories/categories.action';
import { useCategoriesStore } from '../../features/categories/categories.store';
import { v4 as uuidv4 } from 'uuid';
import { DrillDownModel } from '@diagram/model/drilldown.model';

type Props = {
  formdata: Partial<DrillDownModel>;
  setFormData: (data: Partial<DrillDownModel>) => void;
  // setFormData: ({[key: string]: string}) =>  void;
};

const ComboBox = ({ formdata, setFormData }: Props) => {
  const [query, setQuery] = useState('');
  const categories = useCategoriesStore((state) => state.items);
  const selectedCategory = formdata.categoryId || '';

  // const activeCategory = categories[selectedCategory];

  useEffect(() => {
    fetchCategories();
  }, []);

  // Updates selected category label
  useEffect(() => {
    if (selectedCategory && categories[selectedCategory]) {
      setFormData({
        ...formdata,
        categoryId: selectedCategory,
        color: categories[selectedCategory].color,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories && selectedCategory]);

  const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
  };

  const handleSelection = (category: Category | null | 'new') => {
    if (typeof category === 'object' && category !== null) {
      setFormData({
        ...formdata,
        categoryId: category.id,
        color: category.color,
      });
    } else if (category === 'new') {
      const id = uuidv4();
      setFormData({ ...formdata, categoryId: id });
      openCategoryModal(id, query, 'create');
    } else {
      setFormData({ ...formdata, categoryId: '' });
    }
  };

  const filteredCategories = Object.values(categories).filter((category) =>
    category.name.toLowerCase().includes(query.toLowerCase())
  );

  const stopPropagation = (e: React.MouseEvent | React.PointerEvent) => {
    e.stopPropagation();
    e.preventDefault();
  };

  const handleEditCategory = (e: React.MouseEvent, id: string) => {
    stopPropagation(e);
    openCategoryModal(id, '', 'update');
  };

  return (
    <Combobox
      as="div"
      value={categories[selectedCategory]}
      onChange={handleSelection}
    >
      <Label className="block text-sm/6 font-medium text-gray-900">
        Category
      </Label>
      <div className="relative mt-2 items-center">
        <ComboboxInput
          className="block w-full rounded-md bg-white py-1.5 pr-12 pl-8 text-sm text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-primary sm:text-sm/6"
          onChange={handleInputChange}
          onBlur={() => setQuery('')}
          displayValue={(category: Category) => category?.name}
        />
        {selectedCategory && (
          <div
            className="absolute left-3 top-1/2 size-2 rounded-full -translate-y-1/2"
            style={{ backgroundColor: categories[selectedCategory]?.color }}
          ></div>
        )}
        <ComboboxButton className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-hidden">
          <ChevronUpDownIcon
            className="size-5 text-gray-400"
            aria-hidden="true"
          />
        </ComboboxButton>
        <ComboboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base ring-1 shadow-lg ring-black/5 focus:outline-hidden sm:text-sm">
          {filteredCategories.length > 0 ? (
            filteredCategories.map((category) => (
              <ComboboxOption
                key={category.id}
                value={category}
                className="group relative cursor-default py-2 pr-9 pl-3 text-gray-900 select-none data-focus:bg-primary data-focus:text-white data-focus:outline-hidden"
              >
                <div className="flex items-center gap-2">
                  <div
                    className="size-2 rounded-full bg-gray-300"
                    style={{ backgroundColor: category.color }}
                  ></div>
                  <span className="block truncate group-data-selected:font-semibold">
                    {category.name}
                  </span>
                </div>

                <span className="absolute inset-y-0 right-0 hidden items-center pr-4 text-primary group-data-focus:text-white group-data-focus:flex group-data-selected:flex  transition-all ease-in-out duration-200">
                  <CheckIcon
                    className="size-5 group-hover:hidden transition-all ease-in-out duration-200"
                    aria-hidden="true"
                  />
                  <button
                    className="group-hover:block hidden transition-all ease-in-out duration-200"
                    onMouseDown={stopPropagation}
                    onPointerDown={stopPropagation}
                    onClick={(e) => handleEditCategory(e, category.id)}
                  >
                    <Cog6ToothIcon className="size-5 " aria-hidden="true" />
                  </button>
                </span>
              </ComboboxOption>
            ))
          ) : (
            <ComboboxOption
              value={null}
              disabled
              className="relative cursor-default select-none py-2 pr-9 pl-3 text-gray-900"
            >
              <span className="block truncate text-xs text-gray-500">
                No categories found
              </span>
            </ComboboxOption>
          )}
          {query && (
            <ComboboxOption
              value={'new'}
              className="group relative cursor-default py-2 pr-9 pl-3 text-gray-900 select-none data-focus:bg-primary data-focus:text-white data-focus:outline-hidden"
            >
              <span className="block truncate group-data-selected:font-semibold text-xs">
                Create "{query}"
              </span>
            </ComboboxOption>
          )}
        </ComboboxOptions>
      </div>
    </Combobox>
  );
};
export default ComboBox;
